/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @angular-eslint/component-selector */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { DeliveryTypes } from 'entities/cart-search';
import { ShippingRateRequest } from 'entities/carts/shipping-rate-request';
import { ShippingRate } from 'entities/carts/shipping-rates';
import { CountryCode } from 'entities/country-code';
import { CurrencyCode } from 'entities/currency-code';
import { Customer } from 'entities/customer';
import { Province } from 'entities/province';
import { ShipToCustomer } from 'entities/ship-to-customer';
import { Observable, combineLatest } from 'rxjs';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { CheckoutFormService } from 'services/checkout-form.service';
import { AppState } from 'store/app-state';
import * as BranchSelectors from 'store/branch/branch.selectors';
import * as CartActions from "store/cart/cart.actions";
import * as CartSelectors from 'store/cart/cart.selectors';
import * as CustomerActions from 'store/customer/customer.actions';
import * as CustomerSelectors from 'store/customer/customer.selectors';
import { SelectedCustomer } from 'store/customer/customer.state';
import * as FeatureFlagSelectors from 'store/feature-flags/feature-flags.selectors';
import * as ProvinceSelectors from "store/province/province.selectors";
import { CheckoutValidationMessages } from '../checkout-validation/checkout-validation.messages';
import { CheckoutShipToForm, CheckoutShipToFormGroup, CheckoutShipToService } from './checkout-ship-to.service';

@Component({
  selector: 'checkout-ship-to',
  templateUrl: './checkout-ship-to.component.html',
  styleUrls: ['./checkout-ship-to.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutShipToComponent implements OnDestroy {

  @Input() edit: Customer | ShipToCustomer = null;
  @Input() showDetails: boolean = true;
  @Input() selectEnabled: boolean = true;
  @Input() inModal: boolean = false;
  @Input() disabled: boolean = false;
  @Output() onCancel: EventEmitter<void> = new EventEmitter();
  @Output() onUpdate: EventEmitter<ShipToCustomer> = new EventEmitter();

  public errorMessages = CheckoutValidationMessages;
  public countryCode: string = '';
  public isCanadaCustomer: boolean = false;
  public isCashDefault: boolean = false;
  public selectedShipTo$: Observable<ShipToCustomer> = this.store.select(CustomerSelectors.selectShipTo);
  public shipToCustomers$: Observable<Customer[]> = this.store.select(CustomerSelectors.selectShipToCustomers);
  public loading$: Observable<boolean> = this.store.select(CustomerSelectors.selectShipToLoading);
  public customer$: Observable<SelectedCustomer> = this.store.select(CustomerSelectors.selectedCustomer);
  public isEditable$: Observable<boolean> = this.store.select(CartSelectors.selectIsQuote).pipe(map((isQuote) => !isQuote));
  public provinceList$: Observable<Province[]> = this.store.select(ProvinceSelectors.selectProvinceList);
  public isShippingRatesLoading$: Observable<boolean> = this.store.select(CartSelectors.selectIsShippingRatesLoading);
  public shippingRates$: Observable<ShippingRate[]> = this.store.select(CartSelectors.selectShippingRates);
  public validateForm$: Observable<[[string, boolean], ShipToCustomer, boolean]> = combineLatest([
    this.store.select(BranchSelectors.selectedBranchCountryCode),
    this.store.select(CartSelectors.deliveryRequiresAddress)
  ]).pipe(
    withLatestFrom(
      this.selectedShipTo$,
      this.store.select(CustomerSelectors.selectShipToEditing)
    ),
    tap(([[countryCode, deliveryRequiresAddress], selectedShipTo, shipToEditing]: [[string, boolean], ShipToCustomer, boolean]) => {
      this.validateFormAfterDeliveryChange(countryCode, deliveryRequiresAddress, selectedShipTo, shipToEditing);
    })
  );
  public isCash: boolean = false;
  public shipToForm: FormGroup<CheckoutShipToFormGroup>;
  public deliveryRequiresAddress: boolean = false;
  public deliveryCosts$: Observable<number> = this.store.select(CartSelectors.selectDeliveryCosts);
  public currencyCode$: Observable<CurrencyCode> = this.store.select(BranchSelectors.selectCurrencyCode);
  public cartId$: Observable<string> = this.store.select(CartSelectors.selectCartId);
  public selectedShippingRate$: Observable<ShippingRate> = this.store.select(CartSelectors.selectedShippingRate).pipe(
    tap(selectedShippingRate => this.selectedShippingRate = selectedShippingRate)
  );
  public shippingManagementEnabled$: Observable<boolean> = this.store.select(FeatureFlagSelectors.isFeatureActive('ShippingManagement'));
  public selectedShippingRate: ShippingRate = null;
  // When the value of the cashCustomerDefault is null, it means we do not need to use it
  public cashCustomerDefault$ = combineLatest([
    this.store.select(CustomerSelectors.selectedCustomer),
    this.store.select(BranchSelectors.selectedBranch),
    this.store.select(CartSelectors.selectCartShipTo),
    this.store.select(CartSelectors.selectCartHeaderDeliveryType)
  ]).pipe(
    map(([customer, branch, shipTo, deliveryType]) =>
      {
        const isPickupDeliveryOptionSelected = [
          DeliveryTypes.pickup.toString(),
          DeliveryTypes.adHoc.toString(),
          DeliveryTypes.selfPick.toString(),
          DeliveryTypes.fcKnown.toString()].includes(deliveryType);

        if(customer.customerNumber === "200000" && ((shipTo == null || shipTo.addressNumber === null || shipTo.addressNumber === '') && isPickupDeliveryOptionSelected)){
          return ({
            addressNumber: customer.customerNumber,
            city: branch.city,
            customerName: "",
            customerName2: "",
            mscAccountNumber: null,
            phoneNumber: "",
            postalCode: branch.zip,
            state: branch.state,
            streetAddress: ""
          } as ShipToCustomer);
        } else {
          return null;
        }
      }
    )
  );

  constructor(
    private checkoutFormService: CheckoutFormService,
    private store: Store<AppState>,
    private shipToValidationService: CheckoutShipToService
  ) { }

  get freightdeliveryoption() {
    return this.shipToForm.get('freightdeliveryoption')?.value;
  }

  initForm(customer?: ShipToCustomer | Customer): void {
    const selected = customer || this.edit;
    this.isCash = selected?.addressNumber === '0000200000';
    this.shipToForm = this.shipToValidationService.initForm(selected, this.deliveryRequiresAddress, this.isCanadaCustomer);
  }

  validateFormAfterDeliveryChange(code: string, deliveryRequiresAddress: boolean, selectedShipTo: ShipToCustomer | Customer, editing: boolean): void {
    this.countryCode = code?.toUpperCase();
    this.isCanadaCustomer = this.countryCode === CountryCode.CA;
    this.deliveryRequiresAddress = deliveryRequiresAddress;

    const cachedEditing = editing && this.shipToForm && this.edit && this.getEditedFromForm();
    const shipToAccount = cachedEditing ? cachedEditing : selectedShipTo;

    this.initForm(shipToAccount);

    if (this.shipToForm.valid && !this.inModal && !editing) {
      this.edit = null;
    }

    if (!this.showDetails) {
      return;
    }

    if (!this.shipToForm.valid || editing) {
      this.edit = shipToAccount;
    }
  }

  lookup(): void {
    this.store.dispatch(CustomerActions.openShipToModal());
  }

  load(defaultCustomer: ShipToCustomer, selectedShipToAddress: Customer, multipleShipToCustomers: boolean): void {
    if (multipleShipToCustomers) {
      this.lookup();
      return;
    }

    this.edit = selectedShipToAddress || this.shipToValidationService.createCustomer();
    this.initForm();
    if(defaultCustomer){
      this.edit = defaultCustomer;
      this.shipToForm.controls.city.patchValue(defaultCustomer.city);
      this.shipToForm.controls.state.patchValue(defaultCustomer.state);
      this.shipToForm.controls.postalCode.patchValue(defaultCustomer.postalCode);
      this.shipToForm.patchValue({
        city: defaultCustomer.city,
        state: defaultCustomer.state,
        postalCode: defaultCustomer.postalCode
      });
    }
    this.store.dispatch(CustomerActions.setShipToEditing({ shipToEditing: true }));
  }

  cancel() {
    this.onCancel.emit();
    this.edit = null;
    this.store.dispatch(CustomerActions.setShipToEditing({ shipToEditing: false }));
  }

  update() {
    const edited: ShipToCustomer = this.edit && this.getEditedFromForm();
    this.store.dispatch(CustomerActions.setShipToAddress({ shipToAddress: edited }));
    this.store.dispatch(CartActions.updateShipTo({ shipToCustomer: edited, isEdited: edited.isEdited, isCartUpdate: true }));
    this.store.dispatch(CustomerActions.setShipToEditing({ shipToEditing: false}));
    this.onUpdate.emit({
      ...this.edit,
      ...edited
    });
    this.edit = null;
  }

  getEditedCustomer(edited: Partial<CheckoutShipToForm> & { salesperson?: string }): ShipToCustomer {
    return {
      customerName: edited.customerName,
      customerName2: this.edit.customerName2,
      addressNumber: this.edit.addressNumber,
      streetAddress: edited.streetAddress,
      city: edited.city,
      state: edited.state,
      postalCode: edited.postalCode,
      phoneNumber: edited.phoneNumber,
      salesperson: edited.salesperson || this.edit.salesperson,
      mscAccountNumber: edited.mscAccountNumber,
      customerNumber: this.edit.customerNumber,
      isEdited: true
    } as ShipToCustomer;
  }

  getEditedFromForm(): ShipToCustomer {
    return this.getEditedCustomer(this.shipToForm.value);
  }

  ngOnDestroy(): void {
    if (this.edit && !this.inModal ){
      this.store.dispatch(CustomerActions.setShipToAddress({ shipToAddress: this.getEditedFromForm()}));
    }
  }

  onFreightDeliveryOptionChange(shippingRate: ShippingRate, cartId: string) {
    const request: ShippingRateRequest = {
      cartId,
      code: shippingRate.code,
      service: shippingRate.service,
      listRate: shippingRate.listRate,
      carrierRate: shippingRate.rate,
      deliveryDate: shippingRate.deliveryDate
    };
    this.selectedShippingRate = shippingRate;
    this.store.dispatch(CartActions.updateShippingRate({ shippingRateRequest: request }));
    this.store.dispatch(CartActions.setDeliveryFee({deliveryCost: shippingRate.listRate}));
  }
}
